import { chain, keyBy } from 'lodash';
import { renderCategoryName } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import { getOfferPerformanceReportRows } from '@bbkAdminComponents/offer-performance/offer-performance-report-rows';
import {
  buildPivotTableExportRows,
  mergePivotExportRows,
} from '@bbkAdminComponents/pivot-table/pivot-table-export';
import { toDictionary, type AppDispatch } from '@bbkAdminRedux/redux_utils';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import {
  funnelsSlice,
  type RestFunnel,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { type Play } from '@bbkAdminRedux/rtkq/play.slice';
import {
  reportsSlice,
  ReportType,
  type GetReportV2Args,
  type OfferPerformanceCtaReport,
  type TrendsReport,
  type useExportCustomersData,
} from '@bbkAdminRedux/rtkq/reports.slice';
import {
  rulesSlice,
  RuleType,
  type RuleMetadata,
} from '@bbkAdminRedux/rtkq/rules.slice';
import { surveyReasonsSlice } from '@bbkAdminRedux/rtkq/survey-reasons.slice';
import { isLossAversion } from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  type RestLossAversion,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { type Dictionary } from '@bbkAdminUtils/utility-types';
import { dataToCsvString } from '@bbkShared/utils/export-file';
import {
  createExperiencePerformanceCalculatedFields,
  DEFAULT_EXPERIENCE_PERF_GROUP_BY,
  filterExperienceTrendsRows,
  getExperiencePerformancePivotValues,
  getExperiencePerformanceVisibleColumns,
  resolveExperiencePerformancePivotRows,
} from './cancel-page-experience-performance-export.config';
import {
  createOfferPerformanceCalculatedFields,
  DEFAULT_OFFER_PERF_GROUP_BY,
  getOfferPerformancePivotValues,
  getOfferPerformanceVisibleColumns,
  resolveOfferPerformancePivotRows,
} from './cancel-page-offer-performance-export.config';

export type ExportCustomersData = ReturnType<typeof useExportCustomersData>;

export type BulkPlayExportSettings = {
  companyInternalName: string;
  isNestedAudiencesEnabled: boolean;
  isMultiBrandFunnelAPIEnabled: boolean;
  mrrActive: boolean;
  isRepurchaseAttributionEnabled: boolean;
  expPerfNav: Partial<{
    groupBy: string[];
    visibleColumns: string[];
    sortField: string;
    sortOrderDesc: boolean;
  }>;
  offerPerfNav: Partial<{
    groupBy: (string | undefined)[];
    visibleColumns: string[];
    sortField: string;
    sortOrderDesc: boolean;
  }>;
  brandLabelsById: Record<string, string>;
};

export type BulkPlayExportContext = {
  start: Date;
  end: Date;
  appId: string;
  companyId: string;
  isDemoData: boolean;
  isStarterFreePlan: boolean;
  dispatch: AppDispatch;
  exportLCOReportingData: ExportCustomersData;
  settings: BulkPlayExportSettings;
};

type DispatchUnwrapResult<T> = { unwrap: () => Promise<T> };

const dispatchQuery = async <T>(
  context: BulkPlayExportContext,
  initiate: (args: never) => unknown,
  args: never
): Promise<T> =>
  (
    context.dispatch(initiate(args) as never) as DispatchUnwrapResult<T>
  ).unwrap();

const fetchReportV2 = async <T>(
  context: BulkPlayExportContext,
  args: Omit<GetReportV2Args, 'appKey'>
): Promise<T> =>
  dispatchQuery(context, reportsSlice.endpoints.getReportV2.initiate, {
    appKey: context.appId,
    ...args,
  } as never);

type CancelPageBrandLookups = {
  rulesMap: Dictionary<RuleMetadata>;
  funnelsMap: Dictionary<RestFunnel>;
  vanityReasonsMap: Dictionary<{ display_name?: string }>;
  offersAndLAsByIdMap: Dictionary<RestOffer | RestLossAversion>;
};

const filterFunnelsForBrand = (
  funnels: RestFunnel[],
  brandId: string
): RestFunnel[] =>
  funnels.filter(
    (funnel) =>
      !brandId ||
      (brandId === DEFAULT_BRAND_ID && funnel.brand_id === undefined) ||
      funnel.brand_id === brandId
  );

const fetchCancelPageBrandLookups = async (
  context: BulkPlayExportContext,
  brandId: string,
  reportContext:
    typeof BBKRequestContext.exp_perf | typeof BBKRequestContext.offer_perf
): Promise<CancelPageBrandLookups> => {
  const rules = await dispatchQuery<RuleMetadata[]>(
    context,
    rulesSlice.endpoints.getRules.initiate,
    {
      appKey: context.appId,
      context: reportContext,
      includeAdditionalRules: [RuleType.SPLIT],
    } as never
  );

  const funnels = context.settings.isMultiBrandFunnelAPIEnabled
    ? await dispatchQuery<RestFunnel[]>(
        context,
        funnelsSlice.endpoints.getLiteFunnels.initiate,
        {
          appKey: context.appId,
          brandId,
          context: reportContext,
        } as never
      )
    : filterFunnelsForBrand(
        await dispatchQuery<RestFunnel[]>(
          context,
          funnelsSlice.endpoints.getFunnels.initiate,
          {
            appKey: context.appId,
            context: reportContext,
          } as never
        ),
        brandId
      );

  const reasonsResponse = await dispatchQuery<{
    vanity_reasons?: Array<{ internal_name: string; display_name?: string }>;
  }>(context, surveyReasonsSlice.endpoints.getReasons.initiate, {
    companyInternalName: context.settings.companyInternalName,
    appKey: context.appId,
    brandId,
    context: reportContext,
  } as never);

  const offers = await dispatchQuery<RestOffer[]>(
    context,
    offersAndLossAversionsSlice.endpoints.getOffers.initiate,
    {
      appKey: context.appId,
      context: reportContext,
    } as never
  );
  const lossAversions = await dispatchQuery<RestLossAversion[]>(
    context,
    offersAndLossAversionsSlice.endpoints.getLossAversions.initiate,
    {
      appKey: context.appId,
      context: reportContext,
    } as never
  );

  return {
    rulesMap: toDictionary(rules),
    funnelsMap: toDictionary(funnels),
    vanityReasonsMap: keyBy(
      (reasonsResponse.vanity_reasons ?? []).map((reason) => ({
        ...reason,
        id: reason.internal_name,
      })),
      (reason) => reason.internal_name
    ),
    offersAndLAsByIdMap: toDictionary([...lossAversions, ...offers]),
  };
};

const fetchCtaTextMap = async (
  context: BulkPlayExportContext,
  offerCtaMap: Array<{ offer_id: string; cta_id: string }>
) => {
  if (!offerCtaMap.length) {
    return {};
  }

  const response = await dispatchQuery<{ result?: Array<{ cta_id: string }> }>(
    context,
    offersAndLossAversionsSlice.endpoints.getCtaTextByOfferIdCtaId.initiate,
    {
      appKey: context.appId,
      body: offerCtaMap,
    } as never
  );

  return keyBy(response.result ?? [], (entry) => entry.cta_id);
};

const getBrandLabel = (context: BulkPlayExportContext, brandId: string) =>
  context.settings.brandLabelsById[brandId] ??
  context.settings.brandLabelsById[DEFAULT_BRAND_ID];

const withPlayExportColumns = (
  exportResult: ReturnType<typeof buildPivotTableExportRows>,
  play: Play,
  includePlayId: boolean
) => {
  const playColumns = [
    ...(includePlayId ? [{ value: 'play_id', label: 'Play ID' as const }] : []),
    { value: 'play_name', label: 'Play Name' as const },
  ];

  return {
    fields: [...playColumns, ...exportResult.fields],
    rows: exportResult.rows.map((row) => ({
      ...(includePlayId ? { play_id: play.id } : {}),
      play_name: play.name,
      ...row,
    })),
  };
};

const resolvePivotTableSort = (
  nav: { sortField?: string; sortOrderDesc?: boolean },
  pivotValues: Array<{ field: string | number }>
): { sortField: string; sortOrderDesc: boolean } => ({
  sortField: nav.sortField ?? String(pivotValues[0]?.field ?? ''),
  sortOrderDesc: nav.sortOrderDesc ?? true,
});

export const exportExperiencePerformanceCsvForPlays = async (
  context: BulkPlayExportContext,
  plays: Play[]
): Promise<string> => {
  const lookupCache = new Map<string, CancelPageBrandLookups>();
  const groupByOpts =
    context.settings.expPerfNav.groupBy ?? DEFAULT_EXPERIENCE_PERF_GROUP_BY;
  const visibleColumns =
    context.settings.expPerfNav.visibleColumns ??
    getExperiencePerformanceVisibleColumns();
  const pivotValues = getExperiencePerformancePivotValues(
    context.settings.isRepurchaseAttributionEnabled
  );
  const pivotRows = resolveExperiencePerformancePivotRows(
    groupByOpts,
    context.settings.isNestedAudiencesEnabled
  );
  const { sortField, sortOrderDesc } = resolvePivotTableSort(
    context.settings.expPerfNav,
    pivotValues
  );
  const includePlayId = plays.length > 1;
  const perPlayExports: ReturnType<typeof buildPivotTableExportRows>[] = [];

  for (const play of plays) {
    const brandId = play.brand_id ?? DEFAULT_BRAND_ID;
    let lookups = lookupCache.get(brandId);
    if (!lookups) {
      lookups = await fetchCancelPageBrandLookups(
        context,
        brandId,
        BBKRequestContext.exp_perf
      );
      lookupCache.set(brandId, lookups);
    }

    const report = await fetchReportV2<TrendsReport>(context, {
      start: context.start,
      end: context.end,
      context: context.isDemoData
        ? BBKRequestContext.reports_filter
        : undefined,
      report_type_requests: [
        {
          report_type: ReportType.trends,
          parameters: {
            group_by_period: 'all',
            group_by: 'funnel_id,audiences,vanity_reason',
          },
        },
      ],
      filters: [[{ field: 'play', operator: 'equals', value: play.id }]],
    });

    const calculatedFields = createExperiencePerformanceCalculatedFields({
      rulesMap: lookups.rulesMap,
      vanityReasonsMap: lookups.vanityReasonsMap,
      funnelsMap: lookups.funnelsMap,
      selectedBrandLabel: getBrandLabel(context, brandId),
    });

    perPlayExports.push(
      withPlayExportColumns(
        buildPivotTableExportRows({
          data: filterExperienceTrendsRows(report.trends ?? []),
          calculatedFields,
          pivotValues,
          pivotRows,
          visibleColumns,
          sortField,
          sortOrderDesc,
        }),
        play,
        includePlayId
      )
    );
  }

  const merged = mergePivotExportRows(perPlayExports);
  return dataToCsvString(merged.rows, merged.fields);
};

export const exportOfferPerformanceCsvForPlays = async (
  context: BulkPlayExportContext,
  plays: Play[]
): Promise<string> => {
  const lookupCache = new Map<string, CancelPageBrandLookups>();
  const groupByOpts = (
    context.settings.offerPerfNav.groupBy ?? DEFAULT_OFFER_PERF_GROUP_BY
  ).filter((value): value is string => Boolean(value));
  const visibleColumns = getOfferPerformanceVisibleColumns(
    context.settings.mrrActive,
    context.settings.offerPerfNav.visibleColumns
  );
  const pivotValues = getOfferPerformancePivotValues({
    isDemoData: context.isDemoData,
    mrrActive: context.settings.mrrActive,
    isRepurchaseAttributionEnabled:
      context.settings.isRepurchaseAttributionEnabled,
  });
  const pivotRows = resolveOfferPerformancePivotRows(
    groupByOpts,
    context.settings.isNestedAudiencesEnabled
  );
  const { sortField, sortOrderDesc } = resolvePivotTableSort(
    context.settings.offerPerfNav,
    pivotValues
  );
  const includePlayId = plays.length > 1;
  const perPlayExports: ReturnType<typeof buildPivotTableExportRows>[] = [];

  for (const play of plays) {
    const brandId = play.brand_id ?? DEFAULT_BRAND_ID;
    let lookups = lookupCache.get(brandId);
    if (!lookups) {
      lookups = await fetchCancelPageBrandLookups(
        context,
        brandId,
        BBKRequestContext.offer_perf
      );
      lookupCache.set(brandId, lookups);
    }

    const report = await fetchReportV2<OfferPerformanceCtaReport>(context, {
      start: context.start,
      end: context.end,
      context: context.isDemoData ? BBKRequestContext.offer_perf : undefined,
      report_type_requests: [
        { report_type: ReportType.offer_performance_funnel_audiences_cta },
      ],
      filters: [[{ field: 'play', operator: 'equals', value: play.id }]],
    });

    const offerPerformanceRows = getOfferPerformanceReportRows(report);
    const offerCtaMap = chain(offerPerformanceRows)
      .map((row) => {
        const contentCta = (
          row as { funnelAudContentCta?: string }
        ).funnelAudContentCta?.split(',');
        if (!contentCta?.length) {
          return null;
        }
        const offerId = contentCta.at(-2);
        const ctaId = contentCta.at(-1);
        if (!offerId || !ctaId) {
          return null;
        }
        return { offer_id: offerId, cta_id: ctaId };
      })
      .compact()
      .uniqBy((entry) => [entry.offer_id, entry.cta_id].join('-'))
      .value();

    const ctasTextMap = await fetchCtaTextMap(context, offerCtaMap);
    const calculatedFields = createOfferPerformanceCalculatedFields({
      rulesMap: lookups.rulesMap,
      funnelsMap: lookups.funnelsMap,
      offersAndLAsByIdMap: lookups.offersAndLAsByIdMap,
      ctasTextMap,
      selectedBrandLabel: getBrandLabel(context, brandId),
      isLossAversion,
      renderCategoryName,
    });

    perPlayExports.push(
      withPlayExportColumns(
        buildPivotTableExportRows({
          data: offerPerformanceRows,
          calculatedFields,
          pivotValues,
          pivotRows,
          visibleColumns,
          sortField,
          sortOrderDesc,
        }),
        play,
        includePlayId
      )
    );
  }

  const merged = mergePivotExportRows(perPlayExports);
  return dataToCsvString(merged.rows, merged.fields);
};
